/* live_v2/grid.js — placement helpers + move-to-position drag.
 *
 * The grid is 12 cols × N auto-rows. Each widget has {x,y,w,h} in cell
 * units. Mobile (<md) drops the grid and uses a flex column whose
 * visual order is driven by the panels array (CSS `order`).
 *
 * Drag model: full move-to-position with cascade reflow. When the user
 * drops, the dragged widget's x/y update to the cursor's cell; any
 * other widget that now overlaps gets pushed DOWN by one row until no
 * overlaps remain (iterating with a sanity cap). Compared to the
 * earlier swap-only model, this lets users drop into empty space and
 * push siblings around naturally.
 */

window.LSGrid = {
  /** CSS for a single cell at desktop (md+). */
  cellStyle(w) {
    const x = Math.max(1, (w.x | 0) + 1);             // grid is 1-indexed
    const y = Math.max(1, (w.y | 0) + 1);
    const cw = Math.max(1, w.w | 0);
    const ch = Math.max(1, w.h | 0);
    return `grid-column: ${x} / span ${cw}; grid-row: ${y} / span ${ch};`;
  },

  /** Per-widget minimum cells, sourced from the registry (which is
   *  hydrated from `widgets.min_cells` via _catalog.js merge). Used by
   *  resize handlers to stop users dragging a panel below its designed
   *  floor — e.g. Top 10 stays ≥ 3×3, Graphs stays ≥ 4×5. Falls back to
   *  1×1 if the widget hasn't been registered yet (defensive, lets
   *  drag/resize keep working during initial load). (Xav 2026-05-30.) */
  _minOf(id) {
    const reg = (typeof window !== 'undefined') && window.LSWidgets;
    const def = reg && reg.get && reg.get(id);
    const mc  = def && def.minCells;
    return {
      w: Math.max(1, (mc && mc.w | 0) || 1),
      h: Math.max(1, (mc && mc.h | 0) || 1),
    };
  },

  /** Do two rects overlap? Standard inclusive-exclusive intervals. */
  overlaps(a, b) {
    return a.x < b.x + b.w
        && a.x + a.w > b.x
        && a.y < b.y + b.h
        && a.y + a.h > b.y;
  },

  /** Internal: cascade-reflow `panels` (mutating) keeping `fixedId` in
   *  place. Used by both moveAndReflow and resizeAndReflow. */
  _reflowKeeping(panels, fixedId) {
    const maxIter = panels.length * 8;
    let changed = true, iter = 0;
    while (changed && iter < maxIter) {
      changed = false;
      iter++;
      for (let i = 0; i < panels.length; i++) {
        for (let j = i + 1; j < panels.length; j++) {
          const a = panels[i];
          const b = panels[j];
          if (!this.overlaps(a, b)) continue;
          // Push whichever isn't the fixed widget. If neither, push later.
          const push = (a.id === fixedId) ? b : (b.id === fixedId) ? a : b;
          push.y += 1;
          changed = true;
        }
      }
    }
  },

  /** Move widget `id` to (newX, newY) inside the 12-col grid and push
   *  any overlapping siblings down until no overlaps remain. Returns a
   *  NEW panels array; never mutates the input.
   */
  moveAndReflow(panels, id, newX, newY) {
    const next = panels.map(w => ({ ...w }));    // shallow clone all
    const moved = next.find(w => w.id === id);
    if (!moved) return next;
    moved.x = Math.max(0, Math.min(12 - moved.w, newX | 0));
    moved.y = Math.max(0, newY | 0);
    this._reflowKeeping(next, id);
    return next;
  },

  /** Resize widget `id` to (newW, newH) and reflow siblings (same cascade
   *  algorithm as moveAndReflow). Clamps newW so the widget stays in
   *  the 12-col grid from its current x and not below the registry's
   *  minCells floor. */
  resizeAndReflow(panels, id, newW, newH) {
    const next = panels.map(w => ({ ...w }));
    const target = next.find(w => w.id === id);
    if (!target) return next;
    const min = this._minOf(id);
    target.w = Math.max(min.w, Math.min(12 - target.x, newW | 0));
    target.h = Math.max(min.h, newH | 0);
    this._reflowKeeping(next, id);
    return next;
  },

  /** Resize-and-move in one shot — used when the user drags the N / W /
   *  NW / NE / SW edges/corners, where the cell's origin shifts as well
   *  as its size. Mirrors resizeAndReflow but also updates x/y. */
  resizeAtAndReflow(panels, id, newX, newY, newW, newH) {
    const next = panels.map(w => ({ ...w }));
    const target = next.find(w => w.id === id);
    if (!target) return next;
    const min = this._minOf(id);
    target.x = Math.max(0, Math.min(11, newX | 0));
    target.y = Math.max(0, newY | 0);
    target.w = Math.max(min.w, Math.min(12 - target.x, newW | 0));
    target.h = Math.max(min.h, newH | 0);
    this._reflowKeeping(next, id);
    return next;
  },

  /** Gravity compaction — pull every panel as far UP as it can go without
   *  overlapping one already settled above it, preserving columns and reading
   *  order. Reclaims the holes that removes/shrinks leave behind (drag/resize
   *  reflow only ever pushes DOWN, never fills gaps). Returns a NEW panels
   *  array; never mutates the input. This is the "Tidy up" action. */
  compact(panels) {
    // Settle in reading order (top→bottom, then left→right) so a panel only
    // ever lands beneath panels that were already above it.
    const order = (panels || [])
      .map(w => ({ ...w }))
      .sort((a, b) => ((a.y | 0) - (b.y | 0)) || ((a.x | 0) - (b.x | 0)));
    const placed = [];
    for (const w of order) {
      let y = w.y | 0;
      while (y > 0 && !placed.some(p => this.overlaps({ ...w, y: y - 1 }, p))) {
        y -= 1;
      }
      w.y = y;
      placed.push(w);
    }
    return placed;
  },

  /** Compute target cell from mouse coordinates relative to the grid
   *  container. Returns { col, row } in 0-indexed cell units. */
  cellFromMouse(ev, gridEl, totalRows) {
    const rect = gridEl.getBoundingClientRect();
    const colW = rect.width  / 12;
    const rowH = rect.height / Math.max(1, totalRows);
    const col  = Math.floor((ev.clientX - rect.left) / colW);
    const row  = Math.floor((ev.clientY - rect.top)  / rowH);
    return {
      col: Math.max(0, Math.min(11, col)),
      row: Math.max(0, row),
    };
  },

  /** flex `order` value for a widget on mobile — position in the layout array. */
  orderOf(layout, id) {
    const i = layout.findIndex(w => w.id === id);
    return i < 0 ? 0 : i;
  },

  /** Legacy swap helper — kept for any callers we haven't migrated yet.
   *  New drag flow uses moveAndReflow. */
  swap(layout, idA, idB) {
    const a = layout.find(w => w.id === idA);
    const b = layout.find(w => w.id === idB);
    if (!a || !b || a === b) return layout;
    const tx = a.x, ty = a.y, tw = a.w, th = a.h;
    a.x = b.x; a.y = b.y; a.w = b.w; a.h = b.h;
    b.x = tx;  b.y = ty;  b.w = tw;  b.h = th;
    return [...layout];
  },

  /** Move a widget to a new (x,y) clamping inside the 12-col grid. */
  moveTo(layout, id, x, y) {
    const w = layout.find(wt => wt.id === id);
    if (!w) return layout;
    w.x = Math.max(0, Math.min(12 - w.w, x | 0));
    w.y = Math.max(0, y | 0);
    return [...layout];
  },
};
